import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
} from 'date-fns';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ar } from 'date-fns/locale/ar';
import 'react-datepicker/dist/react-datepicker.css';
import './datepicker-override.css';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';

import { useAppSelector } from '../../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../../redux/selectors/displaySettingsSelectors';

registerLocale('ar', ar);

interface DateFiltersProps {
  startDate: Date;
  endDate: Date;
  onDateChange: (params: {
    startDate: Date;
    endDate: Date;
    filterId?: string;
  }) => void;
  selectedFilter?: string;
}

export const DateFilters: React.FC<DateFiltersProps> = ({
  startDate,
  endDate,
  onDateChange,
  selectedFilter,
}) => {
  const { t } = useTranslation();
  const isRtl = useAppSelector(selectIsRTL);
  const locale = isRtl ? 'ar' : undefined;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const reorderDatepickerMonths = () => {
    if (!isRtl) return;

    const root = containerRef.current;
    if (!root) return;

    const datepickerRoot = root.querySelector('.react-datepicker');
    if (!datepickerRoot) return;

    const months = Array.from(
      datepickerRoot.querySelectorAll('.react-datepicker__month-container')
    );

    if (months.length < 2) return;

    const parent = months[0].parentElement;
    if (!parent) return;

    // reverse order ONE TIME only
    for (let i = months.length - 1; i >= 0; i--) {
      parent.appendChild(months[i]);
    }
  };

  useEffect(() => {
    // wait for DatePicker to mount DOM
    const timer = setTimeout(() => {
      reorderDatepickerMonths();
    }, 50);

    return () => clearTimeout(timer);
  }, [isRtl]);

  const datePresets = [
    {
      id: 'today',
      label: t('analytics.quickFilters.today'),
      getRange: () => ({
        startDate: startOfDay(new Date()),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      id: 'yesterday',
      label: t('analytics.quickFilters.yesterday'),
      getRange: () => ({
        startDate: startOfDay(subDays(new Date(), 1)),
        endDate: endOfDay(subDays(new Date(), 1)),
      }),
    },
    {
      id: 'thisWeek',
      label: t('analytics.quickFilters.thisWeek'),
      getRange: () => ({
        startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
        endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
      }),
    },
    {
      id: 'lastWeek',
      label: t('analytics.quickFilters.lastWeek'),
      getRange: () => {
        const lastWeek = subWeeks(new Date(), 1);
        return {
          startDate: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          endDate: endOfWeek(lastWeek, { weekStartsOn: 1 }),
        };
      },
    },
    {
      id: 'pastTwoWeeks',
      label: t('analytics.quickFilters.pastTwoWeeks'),
      getRange: () => ({
        startDate: startOfDay(subWeeks(new Date(), 2)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      id: 'thisMonth',
      label: t('analytics.quickFilters.thisMonth'),
      getRange: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      }),
    },
    {
      id: 'lastMonth',
      label: t('analytics.quickFilters.lastMonth'),
      getRange: () => {
        const lastMonth = subMonths(new Date(), 1);
        return {
          startDate: startOfMonth(lastMonth),
          endDate: endOfMonth(lastMonth),
        };
      },
    },
    {
      id: 'thisYear',
      label: t('analytics.quickFilters.thisYear'),
      getRange: () => ({
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date()),
      }),
    },
    {
      id: 'lastYear',
      label: t('analytics.quickFilters.lastYear'),
      getRange: () => {
        const lastYear = subYears(new Date(), 1);
        return {
          startDate: startOfYear(lastYear),
          endDate: endOfYear(lastYear),
        };
      },
    },
    {
      id: 'custom',
      label: t('analytics.quickFilters.custom'),
      getRange: () => ({
        startDate,
        endDate,
      }),
    },
  ];

  const handlePresetClick = (preset: (typeof datePresets)[0]) => {
    const range = preset.getRange();
    setDateRange([null, null]);
    onDateChange({ ...range, filterId: preset.id });
  };

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className="rounded-lg border border-gray-200 bg-white"
    >
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-gray-50"
      >
        <span className="text-sm font-medium text-gray-700">
          {t('analytics.quickFilters.dateRange')}
        </span>
        {isCollapsed ? (
          <HiChevronDown className="h-5 w-5 text-gray-500" />
        ) : (
          <HiChevronUp className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {!isCollapsed && (
        <div className="flex gap-4 border-t border-gray-200 p-4">
          <div
            className={`flex-shrink-0 border-gray-200 ${
              isRtl ? 'border-l pl-4' : 'border-r pr-4'
            }`}
          >
            <div className="flex flex-col space-y-1">
              {datePresets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetClick(preset)}
                  className={`rounded px-4 py-2 text-sm transition-colors ${
                    isRtl ? 'text-right' : 'text-left'
                  } ${
                    selectedFilter === preset.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-grow">
            <div className="w-full" ref={containerRef}>
              <DatePicker
                onChange={(update) => {
                  setDateRange(update as [Date | null, Date | null]);
                  if (Array.isArray(update)) {
                    const [start, end] = update;
                    if (start && end) {
                      onDateChange({
                        startDate: start,
                        endDate: end,
                        filterId: 'custom',
                      });
                    }
                  }
                }}
                startDate={dateRange[0] || undefined}
                endDate={dateRange[1] || undefined}
                selectsRange
                inline
                monthsShown={2}
                locale={locale}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
